import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import { Box } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useNurses } from "../../../hooks/useNurses";
import { NEUTRAL } from "../../../theme/tokens";
import { staffingWeeklyPath } from "../../../utils/staffingRoutes";
import { EmptyState } from "../../UIToolkit/molecules/EmptyState";
import { useLoadedStaffingDays } from "../hooks/useLoadedStaffingDays";
import { MobileGapsStaffing } from "./MobileGapsStaffing";
import { useGapsVisibleWeek } from "./useGapsVisibleWeek";

/** What the shell says instead of the home, when it says anything. */
enum RosterNotice {
	None = "none",
	NoStaff = "no_staff",
	Failed = "failed",
}

/**
 * What to say instead of the home when there is no roster to build it from. `WeekViewProvider` builds no day at
 * all without staff, so the home would be its chrome around nothing. A roster that is still there after a later
 * refetch fails keeps the home: the data on screen is still the department's.
 */
const rosterNoticeFor = (staffCount: number, isRosterLoaded: boolean, isRosterFailed: boolean): RosterNotice => {
	if (staffCount > 0) return RosterNotice.None;
	if (isRosterFailed) return RosterNotice.Failed;
	return isRosterLoaded ? RosterNotice.NoStaff : RosterNotice.None;
};

/**
 * The gaps-first mobile Staffing shell: the day's content, above a fixed bottom bar.
 *
 * The shell owns the URL date; `MobileGapsStaffing` owns what is drawn, including which part scrolls and
 * where pull-to-refresh lives. It drops classic's `MobileStaffingHeader` and `StaffingActionFab`: the bottom
 * bar owns navigation now, and the FAB's actions moved into the More sheet, so keeping either would
 * leave two of everything on one phone screen.
 */
export const MobileGapsShell: FC = () => {
	const { dateString } = useParams();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { chosenDepartment, hospitalData } = useHospitalData();
	const loadedDays = useLoadedStaffingDays();
	// The roster query `HospitalDataProvider` fills (same key; never fetched from here), read for its outcome: an
	// empty roster is only "no staff" when the load SUCCEEDED — a failed one (a 403, the network) must not claim
	// the department has nobody.
	const { isSuccess: isRosterLoaded, isError: isRosterFailed } = useNurses(chosenDepartment.department_id);
	const rosterNotice = rosterNoticeFor(hospitalData.nurses.length, isRosterLoaded, isRosterFailed);
	const { weekIndexForDate, dayInWeek } = useGapsVisibleWeek();

	const selectedDate = useMemo<DateTime>(() => {
		const fromUrl = dateString === undefined ? undefined : DateTime.fromISO(dateString);
		// DEFAULT: an absent or unparseable URL date opens today. The date is a deep-link convenience, not
		// data — a bad one means the link was mistyped or stale, and today is the day a head nurse opening
		// their phone is asking about. Rendering nothing instead would hide a working schedule behind a typo.
		return fromUrl?.isValid ? fromUrl : DateTime.now();
	}, [dateString]);

	// The visible week the URL day is in (see `useGapsVisibleWeek`), by calendar date.
	const weekIndex = weekIndexForDate(selectedDate);

	const handleDateChange = useCallback(
		(next: DateTime): void => {
			navigate(staffingWeeklyPath(chosenDepartment.department_id, next.toFormat("yyyy-MM-dd")));
		},
		[navigate, chosenDepartment.department_id],
	);

	// A valid date the loaded schedule does not cover — a shared link, a stale notification deep link, a
	// bookmark from last month — resolves to no week at all, and every descendant indexes
	// `weeks[weekIndex]`. Redirect onto the nearest loaded day rather than render `weeks[-1]`, mirroring
	// what the classic screen does for the same URLs. Clamping to a real `day.date` guarantees the next
	// lookup matches, so this cannot re-fire.
	//
	// With weeks starting after Sunday, the first loaded week's days before the start day belong to a visible
	// week that was never loaded, so the earliest day to land on is the first visible week's first column.
	useEffect(() => {
		if (weekIndex !== -1 || !loadedDays.length) return;
		const firstShownDay = dayInWeek(0, 0);
		const lastLoadedDay = loadedDays[loadedDays.length - 1];
		if (firstShownDay === undefined) return;
		const clamped = selectedDate < firstShownDay ? firstShownDay : lastLoadedDay;
		navigate(staffingWeeklyPath(chosenDepartment.department_id, clamped.toFormat("yyyy-MM-dd")), { replace: true });
	}, [weekIndex, loadedDays, selectedDate, navigate, chosenDepartment.department_id, dayInWeek]);

	return (
		// No scroller here: the page pins its chrome and scrolls only its list (`GapsListScroller`), which is
		// also where pull-to-refresh lives. No bottom-bar clearance either: `Layout` reserves it for every
		// route at once, because the bar is mounted there and covers pages this one knows nothing about.
		<Box data-testid="gaps-home-root" sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, width: "100%", bgcolor: NEUTRAL[50] }}>
			{rosterNotice === RosterNotice.Failed ? (
				<EmptyState
					dataTestId="gaps-home-roster-failed"
					icon={<ErrorOutlineOutlinedIcon />}
					iconColor="error.main"
					title={t("gaps.empty.rosterFailed")}
					hint={t("gaps.empty.rosterFailedHint")}
					sx={{ py: 8, px: 3 }}
				/>
			) : rosterNotice === RosterNotice.NoStaff ? (
				<EmptyState
					dataTestId="gaps-home-no-staff"
					icon={<GroupOutlinedIcon />}
					title={t("gaps.empty.noStaff")}
					hint={t("gaps.empty.noStaffHint")}
					sx={{ py: 8, px: 3 }}
				/>
			) : (
				// Keyed by department so a switch starts from a clean screen — the filter, tab and open groups are one
				// department's. `HospitalDataProvider` already remounts everything while a new department loads; the key
				// keeps that true here without relying on its loading screen.
				<MobileGapsStaffing key={chosenDepartment.department_id} date={selectedDate} onDateChange={handleDateChange} />
			)}
		</Box>
	);
};
